/**
 * InterQuest — affiliate link builders.
 *
 * All outbound monetisable clicks should route through one of the helpers
 * below so we have a single file to:
 *   1. swap in real affiliate IDs once partner programs approve us
 *   2. wrap every click with consistent UTM/source tracking
 *   3. add new networks (or fall back to non-affiliated deep-links) without
 *      hunting through component code
 *
 * ─── How to activate affiliate IDs ────────────────────────────────────────
 *  Add the following keys to /app/frontend/.env (do NOT commit real IDs).
 *  After updating, rebuild the frontend (`yarn build`) for the values to
 *  take effect — CRA bakes env vars in at build time.
 *
 *    REACT_APP_TRAINLINE_TAG=...       # Trainline Partner Solutions partner ID
 *    REACT_APP_BOOKING_AID=...         # Booking.com affiliate ID (numeric)
 *    REACT_APP_HOSTELWORLD_AID=...     # Hostelworld affiliate ID (via CJ)
 *    REACT_APP_GYG_PARTNER_ID=...      # GetYourGuide partner code
 *    REACT_APP_OMIO_PARTNER_ID=...     # Omio Awin merchant ID (mid)
 *    REACT_APP_OMIO_AWIN_AFFID=...     # Your Awin publisher ID
 *    REACT_APP_INTERRAIL_PARTNER_ID=...# Interrail / Eurail affiliate code
 *
 *  Until they're set we still surface the deep link with utm_source so
 *  clicks remain trackable in partner dashboards once we're approved.
 */

const TRAINLINE_TAG   = process.env.REACT_APP_TRAINLINE_TAG   || "";
const BOOKING_AID     = process.env.REACT_APP_BOOKING_AID     || "";
const HOSTELWORLD_AID = process.env.REACT_APP_HOSTELWORLD_AID || "";
const GYG_PARTNER_ID  = process.env.REACT_APP_GYG_PARTNER_ID  || "";
const OMIO_PARTNER_ID = process.env.REACT_APP_OMIO_PARTNER_ID || "";
const OMIO_AWIN_AFFID = process.env.REACT_APP_OMIO_AWIN_AFFID || "";
const INTERRAIL_PARTNER_ID = process.env.REACT_APP_INTERRAIL_PARTNER_ID || "";

/* Common UTM params attached to every outbound link */
const utm = (campaign) => ({
  utm_source: "interquest",
  utm_medium: "affiliate",
  utm_campaign: campaign,
});

const buildQS = (obj) => {
  const p = new URLSearchParams();
  Object.entries(obj).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  });
  return p.toString();
};

/* ─── Conversion tracking ──────────────────────────────────────────────────
 * Fire on every outbound click so we can wire PostHog / GA later without
 * touching component code. Currently sends to our first-party /track/affiliate
 * endpoint (analytics.py) — fully GDPR-friendly, no third party. */
import { api } from "./api";

export function trackAffiliateClick(network, payload = {}) {
  try {
    const evt = { network, source: payload.source || "unknown", payload };
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV !== "production") console.log("[affiliate]", evt);
    // Fire-and-forget — never block the navigation away from our site.
    api.post("/track/affiliate", evt).catch(() => { /* never throw */ });
    if (window.posthog?.capture) window.posthog.capture("affiliate_click", evt);
  } catch (_) { /* never throw on tracking */ }
}

/* ─── Trainline ────────────────────────────────────────────────────────────
 * Accepts either:
 *   trainlineSearchUrl(fromName, toName, dateISO)             // positional
 *   trainlineSearchUrl({ from, to, dateISO })                 // object
 * Both PlanTrip and FeaturedRouteDetail were calling it differently — this
 * polymorphism keeps both call sites working without a refactor. */
export function trainlineSearchUrl(fromOrObj, to, dateISO) {
  let from = fromOrObj;
  if (fromOrObj && typeof fromOrObj === "object") {
    from = fromOrObj.from;
    to = fromOrObj.to;
    dateISO = fromOrObj.dateISO;
  }
  const qs = buildQS({
    originStation: from,
    destinationStation: to,
    outwardDate: dateISO ? dateISO.slice(0, 10) : undefined,
    partner: TRAINLINE_TAG || undefined,
    ...utm("itinerary_leg"),
  });
  return `https://www.thetrainline.com/book/results?${qs}`;
}

/* ─── Booking.com (hotels + hostels mix) ─────────────────────────────── */
export function bookingCityUrl(cityName, checkinISO = "", nights = 0) {
  let checkout = "";
  if (checkinISO && nights > 0) {
    const d = new Date(checkinISO);
    d.setDate(d.getDate() + nights);
    checkout = d.toISOString().slice(0, 10);
  }
  const qs = buildQS({
    ss: cityName,
    aid: BOOKING_AID || undefined,
    checkin: checkinISO || undefined,
    checkout: checkout || undefined,
    ...utm("accommodation"),
  });
  return `https://www.booking.com/searchresults.html?${qs}`;
}

/* ─── Hostelworld (younger interrailers, our core demo) ───────────────── */
export function hostelworldCityUrl(cityName, checkinISO = "", nights = 0) {
  let checkout = "";
  if (checkinISO && nights > 0) {
    const d = new Date(checkinISO);
    d.setDate(d.getDate() + nights);
    checkout = d.toISOString().slice(0, 10);
  }
  const qs = buildQS({
    city: cityName,
    "date-start": checkinISO || undefined,
    "date-end": checkout || undefined,
    affid: HOSTELWORLD_AID || undefined,
    ...utm("accommodation_hostel"),
  });
  return `https://www.hostelworld.com/search?${qs}`;
}

/* ─── GetYourGuide (tours, museum tickets, food walks) ────────────────── */
export function gygCityUrl(cityName) {
  // GYG's slug format is /s/?q=<encoded city>
  const qs = buildQS({
    q: cityName,
    partner_id: GYG_PARTNER_ID || undefined,
    ...utm("city_tours"),
  });
  return `https://www.getyourguide.com/s/?${qs}`;
}

/** Direct landmark search — used by the map banner's "Don't miss" list. */
export function gygLandmarkUrl(landmark, cityName = "") {
  const q = cityName ? `${landmark} ${cityName}` : landmark;
  const qs = buildQS({
    q,
    partner_id: GYG_PARTNER_ID || undefined,
    ...utm("landmark_ticket"),
  });
  return `https://www.getyourguide.com/s/?${qs}`;
}

/* ─── Omio (rail fallback where Trainline is weak — esp. CEE) ─────────── */
export function omioSearchUrl(fromName, toName, dateISO) {
  // Omio runs through Awin's click router for tracking. When affid is set
  // we route through Awin; otherwise direct.
  const target = `https://www.omio.com/search-frontend/results?` + buildQS({
    departureCity: fromName,
    arrivalCity: toName,
    outboundDate: dateISO ? dateISO.slice(0, 10) : undefined,
    ...utm("itinerary_leg_fallback"),
  });
  if (OMIO_AWIN_AFFID && OMIO_PARTNER_ID) {
    return `https://www.awin1.com/cread.php?` + buildQS({
      awinmid: OMIO_PARTNER_ID,
      awinaffid: OMIO_AWIN_AFFID,
      clickref: "interquest",
      ued: target,
    });
  }
  return target;
}

/* ─── Eurail / Interrail Pass (the meta-product) ─────────────────────── */
export function interrailPassUrl(passType = "global") {
  // passType: "global" | "one-country" | "youth" — only matters for the URL
  // slug. The affiliate code is appended via referral param.
  const slug = passType === "one-country" ? "one-country-pass" : "interrail-global-pass";
  const qs = buildQS({
    referrer: INTERRAIL_PARTNER_ID || "interquest",
    ...utm("pass"),
  });
  return `https://www.interrail.eu/en/interrail-passes/${slug}?${qs}`;
}

/* ─── Generic outbound wrapper ────────────────────────────────────────── */
/** Convenience for components that want one prop:
 *    <a href={affiliateUrl("booking", { city: "Paris" })}>…</a>  */
export function affiliateUrl(network, args = {}) {
  switch (network) {
    case "trainline":   return trainlineSearchUrl(args);
    case "booking":     return bookingCityUrl(args.city, args.checkinISO, args.nights);
    case "hostelworld": return hostelworldCityUrl(args.city, args.checkinISO, args.nights);
    case "gyg":         return gygCityUrl(args.city);
    case "gyg-landmark":return gygLandmarkUrl(args.landmark, args.city);
    case "omio":        return omioSearchUrl(args.from, args.to, args.dateISO);
    case "interrail":   return interrailPassUrl(args.passType);
    default:            return "#";
  }
}

/** Are we live yet? Used to optionally show a "Powered by Trainline" badge
 *  only once we're actually approved (and stop showing nothing meanwhile). */
export const AFFILIATES_ACTIVE = {
  trainline:   Boolean(TRAINLINE_TAG),
  booking:     Boolean(BOOKING_AID),
  hostelworld: Boolean(HOSTELWORLD_AID),
  gyg:         Boolean(GYG_PARTNER_ID),
  omio:        Boolean(OMIO_PARTNER_ID && OMIO_AWIN_AFFID),
  interrail:   Boolean(INTERRAIL_PARTNER_ID),
};
